import { AUDIO_CONFIG } from "./config";
import { playProceduralChime } from "./chime";

/**
 * FileAmbientEngine — plays a produced ambient soundscape FILE (config.ambientSrc)
 * on a seamless loop, with smooth volume cross-fades for enable/disable. Used
 * automatically whenever `ambientSrc` is set in audio/config.js.
 *
 * Exposes the exact same interface as the procedural engine:
 *   fadeIn(seconds) / fadeOut(seconds) / playChime()
 * so the rest of the app (SoundContext, Navbar, Numerology) never changes.
 */
export class FileAmbientEngine {
  constructor(config = AUDIO_CONFIG) {
    this.config = config;
    this.audio = null;
    this.fadeTimer = null;
    this.started = false;
  }

  _init() {
    if (this.started) return;
    const audio = new Audio(this.config.ambientSrc);
    audio.loop = true;
    audio.preload = "auto";
    audio.crossOrigin = "anonymous";
    audio.volume = 0;
    this.audio = audio;
    this.started = true;
  }

  _fadeTo(target, seconds, onDone) {
    clearInterval(this.fadeTimer);
    const steps = Math.max(1, Math.round(seconds * 30));
    const start = this.audio.volume;
    const delta = (target - start) / steps;
    let i = 0;
    this.fadeTimer = setInterval(() => {
      i += 1;
      this.audio.volume = Math.min(1, Math.max(0, start + delta * i));
      if (i >= steps) {
        clearInterval(this.fadeTimer);
        if (onDone) onDone();
      }
    }, (seconds * 1000) / steps);
  }

  async fadeIn(time = this.config.fadeInSeconds) {
    this._init();
    try {
      await this.audio.play();
    } catch (e) {
      /* autoplay guard — resolved by the user gesture that called this */
    }
    this._fadeTo(this.config.volume, time);
  }

  fadeOut(time = this.config.fadeOutSeconds) {
    if (!this.started || !this.audio) return;
    this._fadeTo(0, time, () => {
      try {
        this.audio.pause();
      } catch (e) {
        /* ignore */
      }
    });
  }

  playChime() {
    if (this.config.chimeSrc) {
      const chime = new Audio(this.config.chimeSrc);
      chime.volume = Math.min(1, this.config.chimeVolume);
      chime.play().catch(() => {});
      return;
    }
    playProceduralChime(this.config.chimeVolume);
  }
}
